import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Message, Input, Dropdown, Checkbox } from 'semantic-ui-react';
import { AppointmentMeHey } from './AppointmentMeHeyUtils';
import { getAppointmentStatusInfo } from './CardMakeAppointmentMeHey';
import { formatDateForInput, formatTimeFromString, formatTimeFromDate } from 'react-lib/utils/dateUtils';
import DateTextBox from 'react-lib/apps/common/DateTextBox';
import SearchBoxDropdown from 'react-lib/appcon/common/SearchBoxDropdown';
import ModalMakeAppointmentError from './ModalMakeAppointmentError';

interface ModalCreateAppointmentMeHeyProps {
  open: boolean;
  onClose: () => void;
  // onSave: (appointment: Partial<AppointmentMeHey>) => void;
  editingAppointment?: AppointmentMeHey | null;
  providersOptions: Array<{ key: string; text: string; value: string }>;
  selectedDoctorDSB?: (AppointmentMeHey & { dsb?: any }) | null;
  onEvent: any;
  searchedItemListWithKey?: Record<string, any>;
  runSequence: any;
  MakeAppointmentMeHeySequence?: any;
}

interface FormData {
  appointmentId?: number;
  doctor: string;
  date: string;
  startTime: string;
  endTime: string;
  appointmentTime: string;
  manualTime: boolean;
  patientID: string | null;
  patientName: string;
  phoneNumber: string;
  details: string;
}
const AMH = "AMH";
const KEY_PATIENT = `ProxyPatientHasDivisionAdminList_${AMH}`;

const ModalCreateAppointmentMeHey: React.FC<ModalCreateAppointmentMeHeyProps> = (
  props
) => {
  const [formData, setFormData] = useState<FormData>({
    doctor: '',
    date: '',
    startTime: '',
    endTime: '',
    appointmentTime: '',
    manualTime: false,
    patientID: null,
    patientName: '',
    phoneNumber: '',
    details: ''
  });

  // console.log("ModalCreateAppointmentMeHey props.searchedItemListWithKey", props.searchedItemListWithKey)
  // console.log('ModalCreateAppointmentMeHey formData: ', formData);

  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);

  // Check if we're creating from selected appointment (readonly mode)
  const isReadonlyMode = true;
  // const isReadonlyMode = !props.editingAppointment && props.selectedDoctorDSB && props.selectedDoctorDSB.dsb;

  // Generate time slots (every 30 minutes)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          key: time,
          text: time,
          value: time
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Filter appointment time slots to be within service time range
  const appointmentTimeSlots = useMemo(() => {
    if (!formData.startTime || !formData.endTime) {
      return timeSlots;
    }

    const [startHour, startMinute] = formData.startTime.split(':').map(Number);
    const [endHour, endMinute] = formData.endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    return timeSlots.filter(slot => {
      const [hour, minute] = slot.value.split(':').map(Number);
      const slotMinutes = hour * 60 + minute;
      return slotMinutes >= startMinutes && slotMinutes <= endMinutes;
    });
  }, [formData.startTime, formData.endTime, timeSlots]);

  const patientFullName = useMemo(() => {
    return [
      props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_pre_name,
      props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_first_name,
      props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_last_name
    ].filter(Boolean).join(' ');
  }, [props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail])

  // Initialize form with appointment data if editing or from selected appointment
  useEffect(() => {
    if (props.editingAppointment && props.selectedDoctorDSB && props.selectedDoctorDSB.dsb) {
      const dsb = props.selectedDoctorDSB.dsb;
      console.log('props.editingAppointment: ', props.editingAppointment);
      console.log('props.selectedDoctorDSB.dsb: ', props.selectedDoctorDSB.dsb);
      // Editing existing appointment
      const startTime = props.selectedDoctorDSB.startTime;
      const endTime = props.selectedDoctorDSB.endTime;

      const startTimeStr = `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`;
      // console.log('startTimeStr: ', startTimeStr);
      const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
      const serviceDateFormatted = `${startTime.getDate().toString().padStart(2, '0')}/${(startTime.getMonth() + 1).toString().padStart(2, '0')}/${startTime.getFullYear()}`;

      let appointmentTime = props.editingAppointment?.estimated_at ? formatTimeFromDate(new Date(props.editingAppointment.estimated_at)) : startTimeStr;

      // const patientFullName = `${props.MakeAppointmentMeHeySequence?.appointment?.patient_pre_name ? `${props.MakeAppointmentMeHeySequence?.appointment?.patient_pre_name} ` : ''}${props.MakeAppointmentMeHeySequence?.appointment?.patient_first_name ? `${props.MakeAppointmentMeHeySequence?.appointment?.patient_first_name} ` : ''}${props.MakeAppointmentMeHeySequence?.appointment?.patient_last_name || ""}`
      // const patientFullName = [
      //   props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_pre_name,
      //   props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_first_name,
      //   props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient_last_name
      // ].filter(Boolean).join(' ');

      let f = {
        appointmentId: props.editingAppointment.id,
        doctor: dsb.doctor_provider_id,
        date: serviceDateFormatted,
        startTime: startTimeStr,
        endTime: endTimeStr,
        appointmentTime: appointmentTime,
        manualTime: appointmentTimeSlots?.map((slot) => slot.value).includes(appointmentTime) ? false : true,
        patientID: props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient || null,
        patientName: patientFullName,
        phoneNumber: props.editingAppointment.tel || '',
        details: props.editingAppointment.order_note || ''
      }
      console.log('f: ', f);
      setFormData(f);
    } else if (props.selectedDoctorDSB && props.selectedDoctorDSB.dsb) {
      // Creating from selected appointment slot
      const dsb = props.selectedDoctorDSB.dsb;

      // Format date from service_date (YYYY-MM-DD) to DD/MM/YYYY
      const startTime = props.selectedDoctorDSB.startTime;
      const endTime = props.selectedDoctorDSB.endTime;

      const startTimeStr = `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`;
      // console.log('startTimeStr: ', startTimeStr);
      const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
      // console.log('endTimeStr: ', endTimeStr);

      const serviceDateFormatted = `${startTime.getDate().toString().padStart(2, '0')}/${(startTime.getMonth() + 1).toString().padStart(2, '0')}/${startTime.getFullYear()}`;

      setFormData({
        doctor: dsb.doctor_provider_id || '',
        date: serviceDateFormatted,
        startTime: startTimeStr,
        endTime: endTimeStr,
        appointmentTime: formatTimeFromString(dsb.start_time),
        manualTime: false,
        patientID: null,
        patientName: '',
        phoneNumber: '',
        details: ''
      });
    } else {
      // Reset form for new appointment
      const today = new Date();
      setFormData({
        doctor: '',
        date: formatDateForInput(today),
        startTime: '09:00',
        endTime: '12:00',
        appointmentTime: '09:00',
        manualTime: false,
        patientID: null,
        patientName: '',
        phoneNumber: '',
        details: ''
      });
    }
    setErrors({});
  }, [props.editingAppointment, props.selectedDoctorDSB, props.open]);

  // const parseDate = (dateStr: string): Date => {
  //   const [day, month, year] = dateStr.split('/').map(Number);
  //   return new Date(year, month - 1, day);
  // };

  // const parseTime = (timeStr: string, baseDate: Date): Date => {
  //   const [hours, minutes] = timeStr.split(':').map(Number);
  //   const date = new Date(baseDate);
  //   date.setHours(hours, minutes, 0, 0);
  //   return date;
  // };

  const handleSelectedItem = useCallback(
    (data) => async (value: any, key: any) => {
      console.log('value: ', value);

      console.log('props.searchedItemListWithKey: ', props.searchedItemListWithKey);
      // find full name 
      let patient = props.searchedItemListWithKey?.[KEY_PATIENT]?.find((item: any) => {
        return item.proxy_patient === value;
      });
      if (patient) {
        let fullName = [patient.pre_name || '', patient.first_name || '', patient.last_name || ''].join(' ').trim();
        handleChange('patientName', fullName);
      }

      handleChange('patientID', value)
    },
    [props.searchedItemListWithKey]
  );

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: boolean } = {};

    if (!formData.doctor) newErrors.doctor = true;
    if (!formData.patientName) newErrors.patientName = true;
    if (!formData.appointmentTime && !formData.manualTime) newErrors.appointmentTime = true;

    setErrors(newErrors);
    console.log('newErrors: ', newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("handleSubmit formData", formData)
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // const baseDate = parseDate(formData.date);
      // const startDateTime = parseTime(formData.startTime, baseDate);
      // const endDateTime = parseTime(formData.endTime, baseDate);

      // Simulate save delay
      await props.runSequence({
        sequence: "MakeAppointmentMeHey",
        action: "SAVE_APPOINTMENT",
        appointmentData: formData,
        dsb: props.selectedDoctorDSB?.dsb || null,
      })
      // props.onSave(appointmentData);
      // setShowSuccess(true);

      // Auto close after showing success message
      // setTimeout(() => {
      //   setShowSuccess(false);
      //   props.onClose();
      // }, 2000);

    } catch (error) {
      console.error('Error saving appointment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const handleClose = () => {
    setErrors({});
    props.onClose();
  };


  let statusInfo: any = {}
  if (props.editingAppointment?.status) {
    statusInfo = getAppointmentStatusInfo(props.editingAppointment.status);
  }


  const mapOptions = useCallback((items: any) => {
    return items.map((item: any) => {
      let name = [item.pre_name || '', item.first_name || '', item.last_name || ''].join(' ').trim();
      return {
        key: item.proxy_patient,
        value: item.proxy_patient,
        text: name
      }
    });
  }, []);

  console.log("ModalCreateAppointmentMeHey render formData", props.MakeAppointmentMeHeySequence?.errorMessage)
  return (
    <Modal
      id="ModalCreateAppointmentMeHey"
      open={props.open}
      onClose={handleClose}
      size="small"
      className="appointment-modal"
    >
      <Modal.Header>
        {props.editingAppointment ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายละเอียดนัดหมาย</span>
            {props.editingAppointment.status && (
              <span className={`appointment-status-badge ${statusInfo.className}`}>
                {statusInfo.text}
              </span>
            )}
          </div>
        ) : (
          'เพิ่มรายการนัดหมายใหม่'
        )}
      </Modal.Header>

      <Modal.Content>
        {props.MakeAppointmentMeHeySequence?.showSuccess && (
          <Message positive icon="check circle" style={{ marginBottom: '20px' }}>
            <Message.Content id="success-message" >
              <Message.Header>บันทึกข้อมูลนัดหมายสำเร็จ</Message.Header>
            </Message.Content>
          </Message>
        )}

        {props.MakeAppointmentMeHeySequence?.showError && (
          <ModalMakeAppointmentError 
            open={props.MakeAppointmentMeHeySequence?.showError}
            title="ไม่สามารถเพิ่มรายการนัดหมายใหม่ได้"
            subtitle={props.MakeAppointmentMeHeySequence?.errorMessage || "กรุณาลองใหม่อีกครั้งในภายหลัง" }
            onClose={() => {
              props.runSequence({
                sequence: "MakeAppointmentMeHey",
                action: "CLOSE_ERROR_MODAL",
              })
            }}
          />
        )}

        <Form>
          {/* Doctor / Service Provider */}
          <Form.Field required error={errors.doctor}>
            <label>ผู้ให้บริการ</label>
            <Dropdown
              placeholder="--- ระบุชื่อผู้ให้บริการ ---"
              fluid
              selection

              options={props.providersOptions}
              value={formData.doctor}
              // onEdit={(e) => e.preventDefault()}
              // onChange={(e, { value }) => handleChange('doctor', value)}
              // disabled={isReadonlyMode}
              {...(isReadonlyMode && { open: false })}
            // onClick={(e)=>{ e.preventDefault(); return false}}
            />
          </Form.Field>

          {/* Service Date */}
          <Form.Field>
            <label>วันที่ให้บริการ</label>
            <div className={isReadonlyMode ? 'readonly-datebox' : ''}>

              <DateTextBox
                value={formData.date}
                onChange={(date) => handleChange('date', date)}
                placeholder="DD/MM/YYYY"
                useAdDate={false}
                inputFluid={true}
                // disabled={isReadonlyMode}
                {...(isReadonlyMode && { open: false })}
              />
            </div>
          </Form.Field>

          {/* Service Time Range */}
          <Form.Group widths="equal">
            <Form.Field>
              <label>ช่วงเวลาที่ให้บริการ</label>
              <Dropdown
                fluid
                selection
                options={timeSlots}
                value={formData.startTime}
                onChange={(e, { value }) => handleChange('startTime', value)}
                // disabled={isReadonlyMode}
                {...(isReadonlyMode && { open: false })}
              />
            </Form.Field>
            <Form.Field style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              <label style={{ marginBottom: '10px' }}>ถึง</label>
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Dropdown
                fluid
                selection
                options={timeSlots}
                value={formData.endTime}
                onChange={(e, { value }) => handleChange('endTime', value)}
                {...(isReadonlyMode && { open: false })}
              />
            </Form.Field>
          </Form.Group>

          {/* Appointment Time */}
          <Form.Group widths="equal">
            <Form.Field required error={errors.appointmentTime}>
              <label>เวลานัดหมาย</label>
              {formData.manualTime ? (
                <Input
                  fluid
                  type="time"
                  value={formData.appointmentTime}
                  onChange={(e, { value }) => { 
                    if (!["ยกเลิกนัดหมาย", 'ปฏิเสธโดย Provider', "เสร็จสิ้น"].includes(statusInfo?.text)) {
                      handleChange('appointmentTime', value)}
                    }
                  }
                  placeholder="HH:mm"
                />
              ) : (
                <Dropdown
                  fluid
                  selection
                  options={appointmentTimeSlots}
                  value={formData.appointmentTime}
                  onChange={(e, { value }) => {
             
                    handleChange('appointmentTime', value)}
                    
                  } 
                  {...(["ยกเลิกนัดหมาย", 'ปฏิเสธโดย Provider', "เสร็จสิ้น"].includes(statusInfo?.text) && { open: false })}
                />
              )}
            </Form.Field>
            <Form.Field style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: "10px" }}>
              <Checkbox
                label="กำหนดเวลาแบบ manual"
                checked={formData.manualTime}
                onChange={(e, { checked }) => {
                  if (!["ยกเลิกนัดหมาย", 'ปฏิเสธโดย Provider', "เสร็จสิ้น"].includes(statusInfo?.text)) {
                    handleChange('manualTime', checked)
                  }
                }}
              />
            </Form.Field>
          </Form.Group>

          {/* Patient Name */}
          <Form.Field required error={errors.patientName}>
            <label>ชื่อผู้รับบริการ</label>
            { props.editingAppointment ? <Input value={patientFullName || "-"} /> : <SearchBoxDropdown
              onEvent={props.onEvent}
              type="ProxyPatientHasDivisionAdminList"
              id={AMH}
              style={{ width: "100%" }}
              fluid={true}
              useWithKey={true}
              inline={true}
              icon="search"
              placeholder={"ค้นหาชื่อ - นามสกุล"}
              limit={20}
              mapOptions={mapOptions}
              searchedItemListWithKey={props.searchedItemListWithKey}
              selectedItem={formData.patientID || null}
              setSelectedItem={handleSelectedItem({ name: "patientID" })}
              onAdditionalUrlParams={() => ({ active: true })}

            />}
          </Form.Field>

          {/* Phone Number */}
          <Form.Field>
            <label>เบอร์โทรติดต่อ</label>
            <Input
              // 
              value={props.editingAppointment ? props.editingAppointment?.tel || "-" : formData.patientID ? props.searchedItemListWithKey?.[KEY_PATIENT]?.find((item: any) => {
                return item.proxy_patient === formData.patientID
              })?.phone_no || "-" : "-"}
            // value={formData.phoneNumber}
            // onChange={(e) => handleChange('phoneNumber', e.target.value)}
            />
          </Form.Field>

          {/* Appointment Details */}
          <Form.Field>
            <label>รายละเอียดการนัดหมาย</label>
            <Form.TextArea
              placeholder="ระบุรายละเอียดนัดหมาย"
              value={formData.details}
              onChange={(e) => {
                if (!["ยกเลิกนัดหมาย", 'ปฏิเสธโดย Provider', "เสร็จสิ้น"].includes(statusInfo?.text)) {
                  handleChange('details', e.target.value)
                }
              }}
              rows={3}
            />
          </Form.Field>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          ปิดหน้าจอ
        </Button>
        {!["ยกเลิกนัดหมาย", 'ปฏิเสธโดย Provider', "เสร็จสิ้น"].includes(statusInfo?.text) ? <Button
          color="pink"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading}
        >
          {props.editingAppointment ? "แก้ไขข้อมูล" : "บันทึกข้อมูล"}
        </Button> : null}

      </Modal.Actions>
    </Modal>
  );
};

export default ModalCreateAppointmentMeHey;